import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import {
  FigureIntervalResidual,
  FigureStressWindows,
  FigureCoverageGeometry,
  EvidencePlate,
  MemoEyebrow,
  SectionNum,
  Rule,
  CtaPrimary,
  CtaGhost,
} from '@/components/Patterns';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';

/* Copy is LOCKED — every visible sentence is preserved verbatim. */

const ArtifactBadge = () => (
  <span className="inline-flex items-center text-[0.68rem] font-mono uppercase tracking-[0.08em] px-2 py-0.5 border border-memo-line text-memo-muted flex-shrink-0">
    Illustrative Example
  </span>
);

/* ── Charts (paper-system restart of the demo artifacts; data, labels and
      randomized demo behavior preserved exactly) ─────────────────────── */

// Case 01: Weekday residual bias bar chart
const WeekdayResidualChart = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const residuals = [-12, -18, 4, 7, -22, 9, 3];
  const maxAbs = 25;
  return (
    <div>
      <div className="flex items-end gap-1 h-24 mb-1">
        {days.map((day, i) => {
          const v = residuals[i];
          const pct = (Math.abs(v) / maxAbs) * 100;
          const neg = v < 0;
          const barColor = neg ? 'var(--memo-warn)' : 'var(--memo-ink)';
          return (
            <div key={day} className="flex-1 flex flex-col items-center h-full justify-center gap-0.5">
              {!neg && <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, backgroundColor: barColor }} />}
              <div className="w-px h-px" style={{ backgroundColor: 'var(--memo-line-2)' }} />
              {neg && <div className="w-full rounded-b-sm" style={{ height: `${pct}%`, backgroundColor: barColor }} />}
              {!neg && <div style={{ height: `${pct}%` }} />}
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mb-1">
        {days.map((d) => <div key={d} className="flex-1 text-center text-[10px] font-mono text-memo-muted">{d}</div>)}
      </div>
      <div className="h-px" style={{ backgroundColor: 'var(--memo-line-2)' }} />
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-warn)' }} /><span className="text-[10px] font-mono text-memo-muted">Negative bias (below zero)</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-ink)' }} /><span className="text-[10px] font-mono text-memo-muted">Positive</span></div>
      </div>
    </div>
  );
};

// Case 02: Stress interval recurrence heatmap (randomized demo behavior retained)
const RecurrenceHeatmap = () => {
  const HOURS = Array.from({ length: 12 }, (_, i) => `${String(i + 8).padStart(2, '0')}`);
  const DAYS = ['M', 'T', 'W', 'T', 'F'];
  const getIntensity = (day, hour) => {
    if ((hour === 2 || hour === 3) && day < 4) return 'var(--memo-warn)';
    if ((hour === 2 || hour === 3) && day === 4) return 'var(--memo-accent)';
    if (hour === 6 && day > 1) return 'var(--memo-accent)';
    if (hour === 7 && day > 1 && day < 4) return 'var(--memo-warn)';
    return Math.random() > 0.85 ? 'var(--memo-signal-2)' : 'var(--memo-line)';
  };
  const legend = [
    ['var(--memo-paper-2)', 'Low'],
    ['var(--memo-line-2)', 'Moderate'],
    ['var(--memo-signal-2)', 'Elevated'],
    ['var(--memo-warn)', 'Critical'],
  ];
  return (
    <div>
      <div className="flex gap-0.5 mb-1 ml-5">
        {HOURS.map((h) => <div key={h} className="flex-1 text-[9px] font-mono text-memo-muted text-center">{h}</div>)}
      </div>
      <div className="space-y-0.5">
        {DAYS.map((day, di) => (
          <div key={`${day}-${di}`} className="flex items-center gap-0.5">
            <span className="text-[10px] font-mono text-memo-muted w-4 flex-shrink-0 text-right">{day}</span>
            {HOURS.map((_, hi) => (
              <div key={hi} className="flex-1 h-5 rounded-sm" style={{ backgroundColor: getIntensity(di, hi) }} />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-2">
        {legend.map(([bg, l]) => (
          <div key={l} className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: bg }} /><span className="text-[10px] font-mono text-memo-muted">{l}</span></div>
        ))}
      </div>
    </div>
  );
};

// Case 03: Queue A vs B utilization divergence
const QueueDivergenceChart = () => {
  const intervals = Array.from({ length: 16 }, (_, i) => {
    const base = 55 + Math.sin(i * 0.5) * 10;
    return { qA: Math.min(95, base + 20 + Math.sin(i * 0.7) * 8), qB: Math.max(20, base - 15 + Math.cos(i * 0.6) * 10) };
  });
  const max = 100;
  return (
    <div>
      <div className="flex items-end gap-1 h-24">
        {intervals.map((d, i) => (
          <div key={i} className="flex-1 flex items-end gap-0.5 h-full">
            <div className="flex-1 rounded-t-sm" style={{ height: `${(d.qA / max) * 100}%`, backgroundColor: 'var(--memo-warn)' }} />
            <div className="flex-1 rounded-t-sm" style={{ height: `${(d.qB / max) * 100}%`, backgroundColor: 'var(--memo-ink-2)' }} />
          </div>
        ))}
      </div>
      <div className="h-px mt-1 mb-2" style={{ backgroundColor: 'var(--memo-line-2)' }} />
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-warn)' }} /><span className="text-[10px] font-mono text-memo-muted">Queue A (overloaded)</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-ink-2)' }} /><span className="text-[10px] font-mono text-memo-muted">Queue B (idle capacity)</span></div>
      </div>
    </div>
  );
};

// Case 04: Adherence impact heatmap
const AdherenceImpactChart = () => {
  const rows = 5, cols = 14;
  const getColor = (r, c) => {
    const isBreak = (c >= 2 && c <= 4) || (c >= 9 && c <= 11);
    const isPeak = (c >= 2 && c <= 5) || (c >= 9 && c <= 12);
    if (isBreak && isPeak) return 'var(--memo-warn)';
    if (isBreak) return 'var(--memo-accent)';
    if (isPeak) return 'var(--memo-signal-2)';
    return 'var(--memo-paper-2)';
  };
  return (
    <div>
      <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: rows * cols }, (_, i) => {
          const r = Math.floor(i / cols), c = i % cols;
          return <div key={i} className="h-4 rounded-sm" style={{ backgroundColor: getColor(r, c) }} />;
        })}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-warn)' }} /><span className="text-[10px] font-mono text-memo-muted">Break deviation × peak window</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--memo-signal-2)' }} /><span className="text-[10px] font-mono text-memo-muted">Non-amplified</span></div>
      </div>
    </div>
  );
};

// Case 05: CPC drift over time
const CPCDriftChart = () => {
  const pts = Array.from({ length: 36 }, (_, i) => {
    const trend = i * 0.08;
    const noise = Math.sin(i * 0.8) * 0.4 + Math.cos(i * 1.3) * 0.3;
    return { y: 5.5 + trend + noise, i };
  });
  const max = Math.max(...pts.map((p) => p.y));
  const min = Math.min(...pts.map((p) => p.y));
  const W = 440, H = 80;
  const scaleY = (v) => H - ((v - min) / (max - min)) * (H - 10) - 5;
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(p.i / (pts.length - 1)) * W} ${scaleY(p.y)}`).join(' ');
  const area = `${d} L ${W} ${H} L 0 ${H} Z`;
  const midY = scaleY((max + min) / 2);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20">
        <path d={area} fill="rgba(192,83,59,0.06)" />
        <line x1={0} y1={midY} x2={W} y2={midY} stroke="#BFB8A9" strokeWidth={1} strokeDasharray="3 3" />
        <path d={d} fill="none" stroke="#C0533B" strokeWidth={1.5} opacity={0.8} />
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-memo-muted mt-1">
        <span>Month 1</span>
        <span>CPC trend — stable volume, rising cost</span>
        <span>Month 9</span>
      </div>
    </div>
  );
};

// Shared analytical figures deployed where the locked copy names the artifact type.
const SUPPORTING_FIGURES = {
  '01': <FigureIntervalResidual />,
  '02': <FigureStressWindows />,
  '03': <FigureCoverageGeometry />,
};

/* ── Demo Cases ──────────────────────────────────────────────────────── */

const DEMO_CASES = [
  {
    num: '01',
    title: 'Weekday Residual Bias Hidden by "Good Accuracy"',
    domain: 'Demand & Forecast Structure',
    context: 'Multi-queue inbound operation (~230 FTEs). Monthly forecast versus actual accuracy above accepted threshold. Recurring SLA instability relating to weekdays (Tuesdays and Fridays in particular) persists even with stable staffing. No patterns were visible when the earlier data was viewed in aggregate.',
    signals: [
      'Forecast residuals grouped by weekday show strong bias (positive or negative)',
      'Recurring time bands where variance is underestimated',
      'SLA decline depends on the direction of the residual, not its magnitude.',
      'Recovery lag is stable when negative bias occurs',
    ],
    classification: { primary: 'Demand & Forecast Structure', interaction: 'Coverage & Schedule Architecture', exposure: 'Elevated', pathway: 'Directional bias → Coverage rigidity → Threshold amplification' },
    artifacts: ['Residual mean per day of week (interval weighted)', 'Directional Bias Index (weekday stratification)', 'Coverage rigidity overlay (shift blocks versus residual bands)', 'Mapping SLA sensitivity (service collapse vs bias direction)'],
    demonstrates: 'Total accuracy may appear \u2018acceptable\u2019 while directional residual bias balances fixed coverage windows.',
    excerpt: 'Instability is model-structure driven as opposed to execution driven. Rebuilding the schedule without fixing remaining direction is just shifting distortion.',
    chart: <WeekdayResidualChart />,
    figureTag: 'Figure 01 · residual scan',
  },
  {
    num: '02',
    title: 'Recurring Stress Interval Concentration (Fixed 15-30 min Burn Windows)',
    domain: 'Intraday Volatility & Queue Dynamics',
    context: 'Blended operation (approx. 180 FTEs). Volume stable on a daily basis. Headcount stable. SLA breaks down many times over a “fixed window” (i.e. 10:15 to 10:45), which the team cannot avoid.',
    signals: [
      'Stress windows repeat at the same intervals throughout several weeks',
      'Queue depth grows at a constant level',
      'Recovery lag greater than spike duration',
      'Minor arrival shifts can lead to disproportionate SLA collapse (nonlinear response)',
    ],
    classification: { primary: 'Intraday Volatility & Queue Dynamics', interaction: 'Coverage Compression', exposure: 'Elevated', pathway: 'Coverage misdistribution → Threshold sensitivity → Recurrence' },
    artifacts: ['Weekday & interval recurrence matrix (weekday x interval)', 'Elasticity slope curve (Arrival delta > SLA delta)', 'Heat map of coverage compression', 'Fragility band identification'],
    demonstrates: 'This reads as structural rather than random: the pattern repeats beyond what chance explains.',
    excerpt: 'Headcount quantity is not the issue. The system operates inside a fragile elasticity band where minor variance triggers collapse.',
    chart: <RecurrenceHeatmap />,
    figureTag: 'Figure 02 · stress-window matrix',
  },
  {
    num: '03',
    title: 'Skill Rigidity: One Queue Burns While Another Idles',
    domain: 'Coverage & Schedule Architecture',
    context: 'Multi-skill environment. Utilization looks “OK” overall. Queue A stays overloaded while Queue B stays idle. The imbalance persists at a similar demand rate week over week.',
    signals: [
      'Even at the queue level, utilization still varies from week to week',
      'Same demand condition, same bottleneck pattern',
      'Unutilized capacity is concentrated in the less critical skill pool',
      'SLA instability becomes constrained to the highest demanding skill cluster',
    ],
    classification: { primary: 'Coverage & Schedule Architecture', interaction: 'Skill Allocation Rigidity', exposure: 'Moderate', pathway: 'Skill siloing → Elasticity absence → Persistent imbalance' },
    artifacts: ['Skill coverage against demand (per interval) overlay', 'Graph of Queue utilization difference', 'Bottleneck strength index (skill cluster)', 'Redistribution simulation (fixed headcount, limited skills)'],
    demonstrates: 'There could be an adequate level of staffing but skill architecture does not provide elasticity in the critical areas.',
    excerpt: 'The constraint is skill cover design not macro volume. Intervention should focus on building elasticities, not headcount growth.',
    chart: <QueueDivergenceChart />,
    figureTag: 'Figure 03 · coverage geometry',
  },
  {
    num: '04',
    title: '"Adherence is Fine" But Peak Windows Fail',
    domain: 'Workforce Behavior & Execution Variance',
    context: 'Adherence reported 92-95% (stable). Peak instability persists. Leadership believes it is caused by “random drift” or operational inconsistency. No agents have violated their respective policies.',
    signals: [
      'Interval level adherence drift clusters in peak intervals',
      'Small deviations build up at peak intervals',
      'SLA sensitivity is higher during clustered deviation intervals',
      'Aggregate adherence masks impact concentration',
    ],
    classification: { primary: 'Workforce Behavior & Execution Variance', interaction: 'Intraday fragility', exposure: 'Elevated', pathway: 'Micro-deviation clustering → Peak amplification → Fragile intervals' },
    artifacts: ['Impact weighted adherence map (interval weighted)', 'Break density heatmap', 'Clustering coefficient vs. SLA degradation', 'Peak window sensitivity overlay'],
    demonstrates: 'The question is not whether adherence is good, but where deviation sits relative to peak exposure.',
    excerpt: 'This is not an HR enforcement output; it maps structural impact, where small variations grow with timing, peak exposure, and threshold sensitivity.',
    chart: <AdherenceImpactChart />,
    figureTag: 'Figure 04 · adherence heatmap',
  },
  {
    num: '05',
    title: 'Cost per Contact Drift Without Demand Growth',
    domain: 'Cost Structure & Capacity Efficiency',
    context: 'YoY Demand flat. Headcount flat. CPC varies +/- 10, 12% significantly. Pressure from finance without an obvious operational failure. “Market conditions” explanation from leadership. No clear correlation to volume, occupancy, or other operational KPIs.',
    signals: [
      'Idle capacity is localized within low density queues and intervals',
      'Paid and productive distribution are unequal at different intervals',
      'CPC is non-linear when volatility is moderate (i.e., not just a volume effect)',
      'Buffer assumptions act as if distribution was normal. Tail exposure exists',
    ],
    classification: { primary: 'Cost Structure & Capacity Efficiency', interaction: 'Coverage Distribution', exposure: 'Moderate', pathway: 'Distribution inefficiency → Volatility tax → Cost mispricing' },
    artifacts: ['Idle interval accumulation map', 'Paid versus the productive distribution curve', 'Volatility elasticity to CPC', 'Cost breakdown (tax of structure vs tax of volatility)'],
    demonstrates: 'Cost can drift through distribution inefficiency, which amplifies volatility rather than tracking macro demand or staffing.',
    excerpt: 'Unpredictability of cost is structural. Reducing head count would probably enhance vulnerability. Classification helps to avoid the wrongful allocation of cost actions.',
    chart: <CPCDriftChart />,
    figureTag: 'Figure 05 · CPC drift series',
  },
];

/* ── Demo spine rail — How to Read + the five cases. Ids ARE the DOM anchors
   (demo-read + demo-case-N). Labels are concise nav-only aliases derived from
   the case titles; body headings remain verbatim. ── */

const DEMO_RAIL = [
  { id: 'demo-read', label: 'How to Read' },
  { id: 'demo-case-01', num: '01', label: 'Residual Bias' },
  { id: 'demo-case-02', num: '02', label: 'Stress Intervals' },
  { id: 'demo-case-03', num: '03', label: 'Skill Rigidity' },
  { id: 'demo-case-04', num: '04', label: 'Adherence & Peaks' },
  { id: 'demo-case-05', num: '05', label: 'Cost Drift' },
];

const DEMO_RAIL_IDS = DEMO_RAIL.map((item) => item.id);

/* Locked column labels for the classification plate */
const CLASS_FIELDS = [
  ['Primary domain', 'primary'],
  ['Driver', 'interaction'],
  ['Exposure level', 'exposure'],
  ['Pathway', 'pathway'],
];

export function Demo() {
  const activeId = useSectionSpy(DEMO_RAIL_IDS, '0px 0px -80% 0px');
  return (
    <div style={{ backgroundColor: 'var(--memo-field)' }}>
      <SEOHead />

      {/* ── Compact masthead — editorial memorandum head ── */}
      <PageMasthead
        wide
        field="page"
        fieldFocal="0.74,0.30"
        eyebrow="Example Artifacts"
        title="Demo / Example Artifacts"
        intro="Five sample cases showing classification results format, reasoning pattern, and style of artifacts. All content is synthetic."
      >
        <div className="p-4 border border-memo-line-2 bg-memo-paper-2 rounded">
          <p className="text-xs font-mono uppercase tracking-[0.1em]" style={{ color: 'var(--memo-signal)' }}>
            ALL ARTIFACTS BELOW ARE ILLUSTRATIVE — NOT DRAWN FROM LIVE ENGAGEMENTS
          </p>
        </div>
      </PageMasthead>

      {/* ── Shared spine — How to Read + Demo Cases (lg: nav | content) ── */}
      <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <PageNav items={DEMO_RAIL} activeId={activeId} label="Demo sections" />

        <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
          <MobileSectionNav items={DEMO_RAIL} activeId={activeId} label="Demo sections" />

          {/* ── How to Read — narrow editorial passage + supporting evidence ── */}
          <section id="demo-read" className="py-20 md:py-28 bg-memo-paper border-b border-memo-line">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-5">How to Read These Artifacts</h2>
              <ol className="mt-8 flex flex-col gap-0">
                {[
                  'These examples show the format and reasoning style of our outputs; they do not represent any particular finding.',
                  'The classification is based on recurrence + sensitivity + exposure rather than the differences between individual KPIs.',
                  'Outputs are prepared for management review: a memorandum combined with an annex.',
                ].map((t, i) => (
                  <li key={t} className="border-b border-memo-line py-6">
                    <div className="flex items-start gap-5">
                      <SectionNum>{String(i + 1).padStart(2, '0')}</SectionNum>
                      <p className="text-[0.98rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{t}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <EvidencePlate tag="Evidence · interval level">
                <FigureIntervalResidual />
              </EvidencePlate>
            </div>
          </div>
      </section>

          {/* ── Demo Cases — full-width memorandum frames ── */}
          <section className="py-20 md:py-28 border-b border-memo-line">
          <div className="flex flex-col gap-10">
            {DEMO_CASES.map((c) => (
              <div
                key={c.num}
                id={`demo-case-${c.num}`}
                data-testid={`demo-case-${c.num}`}
                className="border border-memo-line-2 bg-memo-paper overflow-hidden rounded"
              >
                {/* Case header */}
                <div className="border-b border-memo-line-2 px-6 md:px-10 py-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <SectionNum>CASE {c.num}</SectionNum>
                    <span className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>{c.domain}</span>
                    <ArtifactBadge />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-medium leading-snug mt-4">{c.title}</h3>
                </div>

                {/* Body */}
                <div className="px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-9">
                  {/* Left: chart plates + context + signals */}
                  <div>
                    <div className="mb-6">
                      <EvidencePlate tag={c.figureTag}>
                        {c.chart}
                      </EvidencePlate>
                    </div>
                    {SUPPORTING_FIGURES[c.num] && (
                      <div className="mt-3 mb-6">
                        <EvidencePlate tag="Supporting figure · annex">
                          {SUPPORTING_FIGURES[c.num]}
                        </EvidencePlate>
                      </div>
                    )}

                    <p className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>Context</p>
                    <p className="mt-2 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{c.context}</p>

                    <div className="mt-7">
                      <p className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>Observed Signals</p>
                      <ul className="mt-2 space-y-2">
                        {c.signals.map((s) => (
                          <li key={s} className="flex items-start gap-3">
                            <span aria-hidden="true" className="text-memo-signal text-sm mt-0.5">—</span>
                            <span className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Right: classification + artifacts + demonstrates + interpretation */}
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>Preliminary Classification</p>
                    <div className="mt-2 p-5 border border-memo-line bg-memo-paper-2 rounded">
                      <dl className="space-y-2">
                        {CLASS_FIELDS.map(([label, key]) => {
                          const v = c.classification[key];
                          const isExposureAlert = label === 'Exposure level' && v === 'Elevated';
                          return (
                            <div key={label} className="flex gap-3 py-0.5">
                              <dt className="font-mono text-xs text-memo-signal w-28 flex-shrink-0 uppercase tracking-[0.08em]">{label}</dt>
                              <dd
                                className="text-[0.85rem]"
                                style={{ color: isExposureAlert ? 'var(--memo-warn)' : ((label === 'Exposure level') ? 'var(--memo-accent)' : 'var(--memo-ink-2)') }}
                              >{v}</dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>

                    <div className="mt-7">
                      <p className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>Artifacts Shown</p>
                      <ul className="mt-2 space-y-1.5">
                        {c.artifacts.map((a) => (
                          <li key={a} className="flex items-start gap-3">
                            <span aria-hidden="true" className="text-memo-signal mt-0.5">◦</span>
                            <span className="text-[0.88rem]" style={{ color: 'var(--memo-muted)' }}>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-7">
                      <p className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color: 'var(--memo-muted)' }}>What This Demonstrates</p>
                      <p className="mt-2 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{c.demonstrates}</p>
                    </div>

                    <div className="mt-7 border-l-2 pl-5 py-2" style={{ borderLeftColor: 'var(--memo-signal)' }}>
                      <p className="font-mono text-xs uppercase tracking-[0.12em] text-memo-signal mb-1">Executive interpretation</p>
                      <p className="text-[0.95rem] leading-relaxed italic" style={{ color: 'var(--memo-ink)' }}>{c.excerpt}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
      </section>
        </div>
      </div>

      {/* ── Footer CTAs — paper sign-off ── */}
      <section className="py-20 md:py-28 bg-memo-paper-2 border-b border-memo-line">
        <div className="container-memo">
          <MemoEyebrow>Continue</MemoEyebrow>
          <div className="flex flex-wrap gap-3 items-center mt-8">
            <CtaPrimary to="/intake" testid="demo-intake-cta">Begin Intake</CtaPrimary>
            <CtaGhost to="/atlas">View Full Atlas</CtaGhost>
            <Link to="/sample-memo" className="inline-flex items-center gap-2 text-[0.95rem] font-medium text-memo-signal hover:text-memo-accent transition-colors duration-150">
              Sample Memo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}